import React from 'react';
import { Formik, Form, Field, CccisdInput, CccisdFieldWrapper, CccisdSelect, CccisdToggle } from 'cccisd-formik';
import Table from 'cccisd-graphql-table';
import _isEqual from 'lodash/isEqual';
import PropTypes from 'prop-types';

import sitesQuery from './sites.graphql';
import youthsQuery from './youths.graphql';
import youthsBySupervisorQuery from './youthsBySupervisor.graphql';
import RequiredFieldLabel from './RequiredFieldLabel';
import style from './style.css';

const Fortress = window.cccisd && window.cccisd.fortress;

const siteColumns = [
    {
        name: 'group.groupId',
        label: 'ID',
        sort: true,
        filter: true,
    },
    {
        name: 'group.label',
        label: 'Site',
        sort: true,
        filter: true,
    },
    {
        name: 'ancestorGroups.agency.group.label',
        label: 'Agency',
        sort: true,
        filter: true,
    },
    {
        name: 'ancestorGroups.organization.group.label',
        label: 'Organization',
        sort: true,
        filter: true,
    },
    {
        name: 'ancestorGroups.collaborator.group.label',
        label: 'Collaborator',
        sort: true,
        filter: true,
    },
];

const youthColumns = [
    {
        name: 'pawn.pawnId',
        label: 'ID',
        hideInTable: true,
    },
    {
        name: 'fields.wrapId',
        label: 'Wrap ID',
        sort: true,
        filter: true,
    },
    {
        name: 'fields.firstName',
        label: 'First Name',
        sort: true,
        filter: true,
    },
    {
        name: 'fields.lastName',
        label: 'Last Name',
        sort: true,
        filter: true,
    },
    {
        name: 'careCoordinatorFullName',
        label: 'Care Coordinator',
        sort: true,
        filter: true,
    },
    {
        name: 'caregiverFullName',
        label: 'Caregiver',
        sort: true,
        filter: true,
    },
];

const CaregiverForm = props => {
    const { onSubmit, initialValues } = props;
    const currentRole = Fortress.user && Fortress.user.acting.role.handle;
    const isSupervisor = currentRole === 'supervisor';

    const onHandleSubmit = async values => {
        await onSubmit({
            ...values,
            site: values.site[0],
            youth: (values.youth || []).map(youth => (youth && typeof youth === 'object' ? youth.pawn.pawnId : youth)),
            role: 'caregiver',
        });
    };

    const validate = values => {
        const errors = {};

        if (!values.caregiverName) {
            errors.caregiverName = 'Full Name is required.';
        }
        if (!values.caregiverPhone && values.receiveMessages) {
            errors.caregiverPhone = 'Phone number is required.';
        }
        if (values.caregiverPhone) {
            if (!/^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/.test(values.caregiverPhone)) {
                errors.caregiverPhone = 'Must be a valid phone number.';
            }
        }
        if (values.caregiverEmail && values.caregiverEmail.length > 0) {
            if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.caregiverEmail)) {
                errors.caregiverEmail = 'Invalid email address.';
            }
        }

        if (!isSupervisor) {
            if (values.site.length < 1) {
                errors.site = 'Site is required.';
            }
            if (values.site.length > 1) {
                errors.site = 'You may select only 1 Site.';
            }
        }

        return errors;
    };

    return (
        <Formik onSubmit={e => onHandleSubmit(e)} validate={e => validate(e)} initialValues={initialValues}>
            {({ handleSubmit, values, setFieldValue, isSubmitting }) => {
                return (
                    <Form>
                        <Field
                            name="caregiverName"
                            component={CccisdInput}
                            label={<RequiredFieldLabel isRequired>Full Name:</RequiredFieldLabel>}
                        />
                        <Field
                            name="caregiverPhone"
                            country="US"
                            component={CccisdInput}
                            label={
                                values.receiveMessages ? (
                                    <RequiredFieldLabel isRequired>Phone Number:</RequiredFieldLabel>
                                ) : (
                                    'Phone Number:'
                                )
                            }
                        />

                        <Field name="caregiverEmail" component={CccisdInput} label="Email:" />

                        <Field name="receiveMessages" component={CccisdToggle} label="Receives Messages" />
                        <Field
                            name="caregiverLanguage"
                            component={CccisdSelect}
                            label="Language:"
                            options={[
                                { value: '', label: '-- Choose language --' },
                                { value: 'en', label: 'English' },
                                { value: 'es', label: 'Spanish' },
                            ]}
                        />
                        {!isSupervisor && (
                            <Field name="site" enableReinitialize>
                                {({ field, form }) => (
                                    <CccisdFieldWrapper
                                        field={field}
                                        form={form}
                                        label={<RequiredFieldLabel isRequired>Site:</RequiredFieldLabel>}
                                    >
                                        <Table
                                            query={sitesQuery}
                                            rowKey="group.groupId"
                                            columns={siteColumns}
                                            csvFilename="Sites.csv"
                                            showRowActions={false}
                                            perPage={10}
                                            onRowClick={row => {
                                                setFieldValue('site', [row['group.groupId']]);
                                                setFieldValue('youth', []);
                                            }}
                                            setRowClass={row =>
                                                row['group.groupId'] === field.value[0] && style.selectedRow
                                            }
                                        />
                                    </CccisdFieldWrapper>
                                )}
                            </Field>
                        )}
                        {values.site && values.site.length === 1 && (
                            <Field name="youth" enableReinitialize>
                                {({ field, form }) => {
                                    return (
                                        <CccisdFieldWrapper
                                            field={field}
                                            form={form}
                                            label={<RequiredFieldLabel>Youth:</RequiredFieldLabel>}
                                        >
                                            <Table
                                                query={isSupervisor ? youthsBySupervisorQuery : youthsQuery}
                                                rowKey="pawn.pawnId"
                                                columns={youthColumns}
                                                csvFilename="Youths.csv"
                                                showRowActions={false}
                                                keepSelectedOnFiltering
                                                rowSelectedCallback={selectedRows => {
                                                    if (!_isEqual(selectedRows, field.value)) {
                                                        setFieldValue('youth', selectedRows);
                                                    }
                                                }}
                                                selectedRows={
                                                    // if the site was just changed, youth should reset; otherwise pre-check the caregiver's existing youth
                                                    !_isEqual(form.values.site, initialValues.site)
                                                        ? []
                                                        : initialValues.youth.map(youth => {
                                                              return youth.pawn.pawnId;
                                                          })
                                                }
                                                graphqlVariables={
                                                    isSupervisor
                                                        ? { pawnId: Fortress.user.acting.id }
                                                        : { siteId: values.site[0] }
                                                }
                                                rowActions={[
                                                    {
                                                        name: 'selectYouth',
                                                        title: 'Link to Youth',
                                                        action: () => {},
                                                    },
                                                ]}
                                            />
                                        </CccisdFieldWrapper>
                                    );
                                }}
                            </Field>
                        )}
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                marginTop: '1em',
                            }}
                        >
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => handleSubmit()}
                                disabled={isSubmitting}
                            >
                                Submit
                            </button>
                        </div>
                    </Form>
                );
            }}
        </Formik>
    );
};

CaregiverForm.propTypes = {
    onSubmit: PropTypes.func,
    initialValues: PropTypes.object,
};

export default CaregiverForm;
